import { useState, useEffect } from "react";
import { DatetimeDropdown } from "../DropdownV2";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { makeStyles } from "@mui/styles";
import { Typography } from "@mui/material";

const SPAN_PALM_COEFFICIENT_DATASET_TYPE = "span-palm-coefficients";
const SPAN_PIT_COEFFICIENT_DATASET_TYPE = "span-pit-coefficients";
const ROOT_GEOJSON_DATASET_TYPE = "root-geojson";

const useStyles = makeStyles((theme) => ({
    dropdownContainer: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

export default function Kickoff({ client, project }) {
    const classes = useStyles();
    const [coefficientType, setCoefficientType] = useState(SPAN_PALM_COEFFICIENT_DATASET_TYPE);
    const [coefficientVersion, setCoefficientVersion] = useState(null);
    const [rootGeojsonVersion, setRootGeojsonVersion] = useState(null);
    const [numSensors, setNumSensors] = useState("");
    const [requiredStructures, setRequiredStructures] = useState([]);
    const [ineligibleStructures, setIneligibleStructures] = useState([]);
    const [structureOptions, setStructureOptions] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!client || !project || !rootGeojsonVersion) {
            setStructureOptions([]);
            return;
        }
        calibrationService
            .getExactDataset(client, project, DEFAULT_GROUP_KEY, ROOT_GEOJSON_DATASET_TYPE, rootGeojsonVersion, true)
            .then((res) => {
                console.log("root-geojson response top-level keys:", Object.keys(res ?? {}), res);
                const names = (res?.features ?? [])
                    .filter((f) => f.properties?.feature_type === "structure")
                    .map((f) => f.properties.structure_name);
                setStructureOptions(names);
            });
    }, [client, project, rootGeojsonVersion]);

    const startValid = client && project && coefficientVersion && rootGeojsonVersion && numSensors;

    const handleSubmit = async () => {
        setSubmitting(true);

        const inputs = [
            {
                dataset_type: ROOT_GEOJSON_DATASET_TYPE,
                group_key: DEFAULT_GROUP_KEY,
                version_dttm: rootGeojsonVersion,
            },
            {
                dataset_type: coefficientType,
                group_key: DEFAULT_GROUP_KEY,
                version_dttm: coefficientVersion,
            },
        ];

        const extra = {
            phases: [1, 3],
            num_sensors: parseInt(numSensors),
            required_structures: requiredStructures,
            reviewed_ineligible_structures: ineligibleStructures,
        };

        try {
            await calibrationService.triggerAspenInputPipeline(client, project, extra, inputs);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Paper variant="outlined" style={{ padding: "24px 32px" }}>
            <div style={{ marginBottom: 24 }}>
                <Typography style={{ textAlign: "center", fontWeight: "bold" }} variant="h4">
                    Kickoff New Placement
                </Typography>
            </div>

            <div>
                <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Build Data Inputs
                </Typography>
                <Typography variant="body1" style={{ marginBottom: 16 }}>
                    Generates inputs for sensor placement.
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={coefficientType}
                    onChange={(_, newType) => {
                        if (newType) {
                            setCoefficientType(newType);
                            setCoefficientVersion(null);
                        }
                    }}
                    size="small"
                    style={{ marginBottom: 16 }}
                >
                    <ToggleButton value={SPAN_PALM_COEFFICIENT_DATASET_TYPE}>Span Palm Coefficients</ToggleButton>
                    <ToggleButton value={SPAN_PIT_COEFFICIENT_DATASET_TYPE}>Span PIT Coefficients</ToggleButton>
                </ToggleButtonGroup>
                <div className={classes.dropdownContainer}>
                    <DatetimeDropdown
                        key={coefficientType}
                        clientName={client}
                        projectName={project}
                        datasetType={coefficientType}
                        group={DEFAULT_GROUP_KEY}
                        value={coefficientVersion}
                        setValue={setCoefficientVersion}
                        name={coefficientType === SPAN_PALM_COEFFICIENT_DATASET_TYPE
                            ? "Span Palm Coefficients Version"
                            : "Span PIT Coefficients Version"}
                        styleClass={{ flex: 1, mx: 2, px: 0 }}
                    />
                    <DatetimeDropdown
                        clientName={client}
                        projectName={project}
                        datasetType={ROOT_GEOJSON_DATASET_TYPE}
                        group={DEFAULT_GROUP_KEY}
                        value={rootGeojsonVersion}
                        setValue={setRootGeojsonVersion}
                        name="Root Geojson Version"
                        styleClass={{ flex: 1, mx: 2, px: 0 }}
                    />
                </div>
                <Divider style={{ marginBottom: 24 }} />
            </div>

            <div>
                <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Placement Configuration
                </Typography>
                <Typography variant="body1" style={{ marginBottom: 16 }}>
                    Generates initial sensor placements.
                </Typography>
                <div className={classes.dropdownContainer}>
                    <TextField
                        label="Number of Sensors"
                        type="number"
                        size="small"
                        value={numSensors}
                        onChange={(e) => setNumSensors(e.target.value)}
                        inputProps={{ min: 1 }}
                        style={{ width: 180 }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={structureOptions.filter((s) => !ineligibleStructures.includes(s))}
                        value={requiredStructures}
                        onChange={(_, value) => setRequiredStructures(value)}
                        renderInput={(params) => <TextField {...params} label="Required Structures" />}
                        style={{ flex: 1 }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={structureOptions.filter((s) => !requiredStructures.includes(s))}
                        value={ineligibleStructures}
                        onChange={(_, value) => setIneligibleStructures(value)}
                        renderInput={(params) => <TextField {...params} label="Ineligible Structures" />}
                        style={{ flex: 1 }}
                    />
                </div>
            </div>

            <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 8 }}
                disabled={!startValid || submitting}
                onClick={handleSubmit}
            >
                {submitting ? "Submitting..." : "Start"}
            </Button>
        </Paper>
    );
}
